import { consoleRoutes, REUSABLE_DEPTH_MAX, WORKFLOW_STEP_MIN_PLAN, planAtLeast } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Field, Icon, Select } from "@sagot/ui";
import { useWorkspaceContext, useWorkspaceDetails } from "../../../../session/workspace-context";
import type { RunWorkflowStep } from "../types";
import { useRefs } from "./refs";
import { type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";
const RUN_PLAN = WORKFLOW_STEP_MIN_PLAN.run_workflow ?? "pro";

/** Run a workflow: pick a reusable workflow; it runs on the same conversation, then this workflow carries on. */
export function RunWorkflowForm({ step, disabled, onChange }: FormProps<RunWorkflowStep>) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const plan = useWorkspaceDetails().data?.plan;
  const { reusables } = useRefs();
  const chosen = reusables.find((workflow) => workflow.id === step.workflow_id);
  const locked = plan !== undefined && !planAtLeast(plan, RUN_PLAN);
  return (
    <div className="space-y-3">
      {locked ? (
        <p className="flex items-center gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note">
          <Icon name="lock" className="size-4 shrink-0" />
          {t(`${F}.reusablePlan`, { plan: t(`console.plans.${RUN_PLAN}`) })}
        </p>
      ) : null}
      <Field label={t(`${F}.reusableWorkflow`)} hint={t(`${F}.reusableHint`, { depth: REUSABLE_DEPTH_MAX })}>
        <Select value={step.workflow_id} disabled={disabled || reusables.length === 0} onChange={(e) => { onChange({ ...step, workflow_id: e.target.value }); }}>
          <option value="">{t(`${F}.choose`)}</option>
          {reusables.map((workflow) => (
            <option key={workflow.id} value={workflow.id}>
              {workflow.state === "live" ? workflow.name : t(`${F}.reusableNotLive`, { name: workflow.name })}
            </option>
          ))}
        </Select>
      </Field>
      {chosen && chosen.state !== "live" ? (
        <p className="flex items-start gap-2 text-xs font-semibold text-warning" role="alert" data-testid="reusable-not-live">
          <Icon name="alert-triangle" className="mt-0.5 size-3.5 shrink-0" />
          {t(`${F}.reusableNotLiveWarning`, { name: chosen.name })}
        </p>
      ) : null}
      {reusables.length === 0 ? (
        <p className="text-xs text-fg-muted" data-testid="reusable-empty">
          {t(`${F}.reusableNone`)}{" "}
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.workflows(summary.slug)}>{t(`${F}.reusableOpenList`)}</AppLink>
        </p>
      ) : null}
      {chosen ? (
        <p className="flex items-center gap-2 text-xs text-fg-muted">
          <Badge>{t(`console.plans.${RUN_PLAN}`)}</Badge>
          <AppLink className="font-semibold text-primary-ink underline" href={consoleRoutes.workflow(summary.slug, chosen.id)}>{t(`${F}.reusableOpen`, { name: chosen.name })}</AppLink>
        </p>
      ) : null}
    </div>
  );
}
